import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAccount } from 'wagmi';
import BigWhiteButton, { BigWhiteButtonContainer } from '../general/BigWhiteButton';
import {
  AmountContainer,
  AnimatedSpacerContainer,
  CenterFoldContainer,
  FoldSymbol,
  MaxButton,
  TopText,
} from '../general/CenterFold';
import ETHLogo from '../general/ETHLogo';
import MEVETHLogo from '../general/MEVETHLogo';
import RewardsChainSelector from './RewardsChainSelector';
import { RewardsPossibleModes } from './RewardsTopFold';

type Props = {
  mode: RewardsPossibleModes;
  setMode: (mode: RewardsPossibleModes) => void;
};

const CHAIN_OPTIONS = [
  { name: 'Ethereum Mainnet', logo: '/svg/chains/ETH.svg' },
  { name: 'Optimism', logo: '/svg/chains/OP.svg' },
  { name: 'Arbitrum', logo: '/svg/chains/ARB.svg' },
];

function ConfirmPanel() {
  const [state, setState] = useState<'Loading' | 'Ready' | 'Error'>('Loading');

  // TODO: Run through team to make sure copy is final.
  let Text = (
    <TopText>
      Your transaction to claim rewards is currently being processed in the mempool. Click{' '}
      <a href="https://etherscan.io/">here</a> to see it on Etherscan.
    </TopText>
  );

  // TODO Report this Error
  if (state === 'Error') {
    Text = <TopText>There was an error processing your transaction. Please try again.</TopText>;
  }

  if (state === 'Ready') {
    Text = (
      <TopText>
        Your transaction to claim rewards has been processed successfully. You should see the
        rewarded mevETH appear in your wallet in the next few seconds.
      </TopText>
    );
  }

  // TODO: Remove
  useEffect(() => {
    setTimeout(() => {
      setState('Ready');
    }, 1250);
  }, []);

  const isLoading = state === 'Loading';
  return (
    <CenterFoldContainer>
      <div style={{ height: 80 }}>{Text}</div>
      <AnimatedSpacerContainer>
        <SpinningDiv style={isLoading ? {} : { animation: 'none' }}>
          <MEVETHLogo height={48} width={48} />
        </SpinningDiv>
      </AnimatedSpacerContainer>
    </CenterFoldContainer>
  );
}

function useSelectedOptions() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selected = CHAIN_OPTIONS[selectedIndex];
  return {
    selected,
    selectedIndex,
    setSelectedIndex,
  };
}

function useMevETHRewards() {
  const [loading, setLoading] = useState(true);
  const [amountOfMevETHRewards, setAmountOfMevETHRewards] = useState(0);

  // TODO: Fetch from contract.
  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
      setAmountOfMevETHRewards(Math.random() * 100);
    }, 1250);
  }, []);

  return { loading, amountOfMevETHRewards };
}

function ClaimPanel({ setMode }: Props) {
  const { address } = useAccount();
  const { selected, selectedIndex, setSelectedIndex } = useSelectedOptions();
  const [advancedAddress, setAdvancedAddress] = useState('');
  const [advancedOptionsEnabled, setAdvancedOptionsEnabled] = useState(false);

  const { loading, amountOfMevETHRewards } = useMevETHRewards();
  const disabled = loading || (address && !amountOfMevETHRewards);

  const onClick = () => {
    // TODO: claim rewards in contract, then.
    setMode('Confirm');
  };

  let text = disabled ? 'No rewards to claim' : 'Claim your rewards';
  if (loading) {
    text = 'Loading...';
  }

  return (
    <CenterFoldContainer>
      <TopText>Claim your accrued rewards from holding mevETH.</TopText>
      <AnimatedSpacerContainer>
        <MaxButton>Claim</MaxButton>
        <AmountContainer>
          <MEVETHText>0.0</MEVETHText>
          <FoldSymbol style={{ width: 'auto' }}>
            <ETHLogo height={32} width={32} />
          </FoldSymbol>
        </AmountContainer>
        <MaxButton>of ETH rewards on</MaxButton>
        <RewardsChainSelector
          options={CHAIN_OPTIONS}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
        />
        <MaxButton onClick={() => setAdvancedOptionsEnabled(!advancedOptionsEnabled)}>
          ({advancedOptionsEnabled ? 'Hide ' : 'Show '}
          advanced options)
        </MaxButton>
        {advancedOptionsEnabled && (
          <AdvancedOptionsContainer>
            <AdressInput
              placeholder="Enter address to send ETH Rewards"
              value={advancedAddress}
              onChange={(e) => setAdvancedAddress(e.target.value)}
            />
          </AdvancedOptionsContainer>
        )}
      </AnimatedSpacerContainer>
      <BigWhiteButtonContainer disabled={true} onClick={() => {}}>
        Coming Soon
      </BigWhiteButtonContainer>
    </CenterFoldContainer>
  );
}

export default function RewardsCenterFold(props: Props) {
  if (props.mode === 'Confirm') {
    return <ConfirmPanel />;
  }
  return <ClaimPanel {...props} />;
}

const MEVETHText = styled.div`
  font-size: 48px;
  font-weight: 500;
  color: #ffffff;
  text-align: right;
`;

const SpinningDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  animation: spin 2s linear infinite;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Advanced options
const AdvancedOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const AdressInput = styled.input`
  margin-bottom: 16px;
  min-width: 280px;
  text-align: center;
  color: #ffffff;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #ffffff;

  padding: 0 16px;
  ::placeholder {
    color: rgba(255, 255, 255, 0.85);
  }
`;
